// Node-graph arrange geometry (ENG-1069). Pure, no imports — a leaf module
// importable under `node --test`, and vendored verbatim into the MCP server
// (aos-mcp-threejs-poc) so the agent's arrange tools and the editor's arrange
// menu produce byte-identical layouts.
//
// Modelled on how Unreal's graph editor arranges a SELECTION (Align / Distribute
// / Straighten) and — the one thing no DCC ships — a whole-graph left-to-right
// `tidyGraph` for the "one call, lay it all out" case the MCP agent needs
// (it has no interactive selection to align).
//
// Contract, shared by every function here:
//   node  = { id, position:{x,y}, width, height, selected? }
//   edge  = { source, target }
//   returns [{ id, position:{x,y} }] — ONLY the nodes that actually move.
//   An op that can't apply (below its minimum node count, no internal edges)
//   returns []. Callers apply the patch and never have to special-case a no-op.
//
// These editors flow left→right (source pins on the left, sink/FinalPose on the
// right), so x is the flow axis and `tidyGraph` layers along it.

const DEFAULT_W = 180
const DEFAULT_H = 60

export const ALIGN_OPS = ['left', 'right', 'centerX', 'top', 'bottom', 'centerY']

const w = (n) => (Number.isFinite(n.width) ? n.width : DEFAULT_W)
const h = (n) => (Number.isFinite(n.height) ? n.height : DEFAULT_H)

// Resolve an id list against the node array, preserving node order and dropping
// ids that aren't present. Selection sets routinely carry stale/foreign ids.
function pick(nodes, ids) {
  const want = new Set(ids)
  return nodes.filter((n) => want.has(n.id))
}

// Emit a patch only when the position actually changed (avoids no-op history
// entries and pointless re-renders).
function patchIfMoved(node, x, y) {
  if (node.position.x === x && node.position.y === y) return null
  return { id: node.id, position: { x, y } }
}

// ── align ────────────────────────────────────────────────────────────────
// Snap one edge (or the centers) of the selected nodes to a common line.
// Horizontal ops (left/right/centerX) move X; vertical ops move Y.
export function alignNodes(nodes, ids, op) {
  const sel = pick(nodes, ids)
  if (sel.length < 2) return []

  const horizontal = op === 'left' || op === 'right' || op === 'centerX'
  let target // the common coordinate; interpretation depends on op

  switch (op) {
    case 'left':    target = Math.min(...sel.map((n) => n.position.x)); break
    case 'right':   target = Math.max(...sel.map((n) => n.position.x + w(n))); break
    case 'top':     target = Math.min(...sel.map((n) => n.position.y)); break
    case 'bottom':  target = Math.max(...sel.map((n) => n.position.y + h(n))); break
    case 'centerX': target = mean(sel.map((n) => n.position.x + w(n) / 2)); break
    case 'centerY': target = mean(sel.map((n) => n.position.y + h(n) / 2)); break
    default: throw new Error(`alignNodes: unknown op '${op}' (expected ${ALIGN_OPS.join('|')})`)
  }

  const patches = []
  for (const n of sel) {
    let { x, y } = n.position
    if (horizontal) {
      if (op === 'left') x = target
      else if (op === 'right') x = target - w(n)
      else x = target - w(n) / 2 // centerX
    } else {
      if (op === 'top') y = target
      else if (op === 'bottom') y = target - h(n)
      else y = target - h(n) / 2 // centerY
    }
    const p = patchIfMoved(n, x, y)
    if (p) patches.push(p)
  }
  return patches
}

// ── distribute ─────────────────────────────────────────────────────────────
// Even edge-to-edge spacing along one axis, with the two extreme nodes pinned
// (Unreal's DistributeHorizontally/Vertically). Needs ≥3 to be meaningful.
export function distributeNodes(nodes, ids, axis) {
  const sel = pick(nodes, ids)
  if (sel.length < 3) return []
  const vertical = axis === 'y'
  const size = vertical ? h : w
  const coord = (n) => (vertical ? n.position.y : n.position.x)
  const center = (n) => coord(n) + size(n) / 2

  const order = [...sel].sort((a, b) => center(a) - center(b))
  const first = order[0]
  const last = order[order.length - 1]
  const span = (coord(last) + size(last)) - coord(first) // outer edge to outer edge
  const sumSize = order.reduce((s, n) => s + size(n), 0)
  const gap = (span - sumSize) / (order.length - 1)

  const patches = []
  let cursor = coord(first)
  for (const n of order) {
    const v = cursor
    cursor += size(n) + gap
    const p = vertical
      ? patchIfMoved(n, n.position.x, v)
      : patchIfMoved(n, v, n.position.y)
    if (p) patches.push(p)
  }
  return patches
}

// ── straighten ───────────────────────────────────────────────────────────
// Make wires run horizontal by giving connected nodes a shared center-Y,
// propagated in flow order (source → target). Only edges whose BOTH ends are
// in the selection count. Approximation vs Unreal: our nodes expose no pin
// offsets, so fan-in/fan-out siblings converge onto the same line rather than
// spreading by pin — linear chains (the common case) straighten exactly.
export function straightenConnections(nodes, edges, ids) {
  const sel = pick(nodes, ids)
  if (sel.length < 2) return []
  const inSel = new Set(sel.map((n) => n.id))
  const internal = edges.filter((e) => inSel.has(e.source) && inSel.has(e.target))
  if (internal.length === 0) return []

  const byIdMap = new Map(sel.map((n) => [n.id, n]))
  // Working center-Y per node, seeded from current geometry.
  const cy = new Map(sel.map((n) => [n.id, n.position.y + h(n) / 2]))

  // Process upstream-first so a chain collapses onto its root's line.
  const ordered = [...internal].sort((a, b) => byIdMap.get(a.source).position.x - byIdMap.get(b.source).position.x)
  for (const e of ordered) cy.set(e.target, cy.get(e.source))

  const patches = []
  for (const n of sel) {
    const y = cy.get(n.id) - h(n) / 2
    const p = patchIfMoved(n, n.position.x, y)
    if (p) patches.push(p)
  }
  return patches
}

// ── tidy (whole-graph layout) ──────────────────────────────────────────────
// Left-to-right layered layout: longest-path layering along edge direction,
// one barycenter pass to order each column, columns spaced by the widest node
// they hold. Deterministic; cycles are tolerated (back-edges just don't push a
// node further right). Returns a patch for every node that moves.
export function tidyGraph(nodes, edges, opts = {}) {
  if (nodes.length === 0) return []
  const gapX = opts.gapX ?? 90
  const gapY = opts.gapY ?? 40
  const startX = opts.startX ?? 0
  const startY = opts.startY ?? 0

  const ids = new Set(nodes.map((n) => n.id))
  const valid = edges.filter((e) => ids.has(e.source) && ids.has(e.target))

  // Longest-path layering via Kahn: indegree-0 nodes start at layer 0; each
  // edge relaxes layer[target] = max(., layer[source]+1). Nodes stuck in a
  // cycle keep whatever layer the acyclic part gave them.
  const indeg = new Map(nodes.map((n) => [n.id, 0]))
  const outAdj = new Map(nodes.map((n) => [n.id, []]))
  for (const e of valid) {
    indeg.set(e.target, indeg.get(e.target) + 1)
    outAdj.get(e.source).push(e.target)
  }
  const layer = new Map(nodes.map((n) => [n.id, 0]))
  const queue = nodes.filter((n) => indeg.get(n.id) === 0).map((n) => n.id)
  const deg = new Map(indeg) // mutable working copy
  while (queue.length) {
    const u = queue.shift()
    for (const v of outAdj.get(u)) {
      if (layer.get(v) < layer.get(u) + 1) layer.set(v, layer.get(u) + 1)
      deg.set(v, deg.get(v) - 1)
      if (deg.get(v) === 0) queue.push(v)
    }
  }

  // Group by column, preserving input order as the stable within-column seed.
  const byIdMap = new Map(nodes.map((n) => [n.id, n]))
  const columns = []
  nodes.forEach((n, i) => {
    const L = layer.get(n.id)
    ;(columns[L] ||= []).push({ id: n.id, seed: i })
  })

  // Predecessor lists (for the barycenter ordering pass).
  const preds = new Map(nodes.map((n) => [n.id, []]))
  for (const e of valid) preds.get(e.target).push(e.source)

  // Order each column left→right: column 0 keeps its seed order; later columns
  // sort by the average position of their predecessors in the previous column.
  const rowIndex = new Map() // id → its final row within its column
  for (let L = 0; L < columns.length; L++) {
    const col = columns[L] || []
    if (L > 0) {
      col.sort((a, b) => barycenter(a.id, preds, rowIndex, a.seed) - barycenter(b.id, preds, rowIndex, b.seed))
    } else {
      col.sort((a, b) => a.seed - b.seed)
    }
    col.forEach((c, row) => rowIndex.set(c.id, row))
  }

  // X per column = cumulative widest-node width + gap; nodes centered in column.
  const colWidth = columns.map((col) => Math.max(...(col || []).map((c) => w(byIdMap.get(c.id))), 0))
  const colX = []
  let x = startX
  for (let L = 0; L < columns.length; L++) {
    colX[L] = x
    x += colWidth[L] + gapX
  }

  const patches = []
  for (let L = 0; L < columns.length; L++) {
    const col = columns[L] || []
    let cursor = startY
    for (const c of col) {
      const node = byIdMap.get(c.id)
      const nx = colX[L] + (colWidth[L] - w(node)) / 2
      const ny = cursor
      cursor += h(node) + gapY
      const p = patchIfMoved(node, nx, ny)
      if (p) patches.push(p)
    }
  }
  return patches
}

function barycenter(id, preds, rowIndex, seed) {
  const ps = preds.get(id).filter((p) => rowIndex.has(p))
  if (ps.length === 0) return seed // no placed predecessor → keep seed order
  return mean(ps.map((p) => rowIndex.get(p)))
}

function mean(xs) {
  return xs.reduce((s, v) => s + v, 0) / xs.length
}

// ── keyboard mapping ───────────────────────────────────────────────────────
// Map a keydown to an arrange op token, using Unreal's graph-editor chords:
//   Shift+A/D/W/S      → align left / right / top / bottom
//   Shift+Alt+W / +S   → align vertical-center (middle) / horizontal-center
//   Q                  → straighten connections
// Returns null when the event isn't an arrange chord. Distribute and tidy have
// no Unreal default keys, so they're menu-only. Pure so the mapping is pinned
// by tests rather than living inside a component's keydown handler.
export function arrangeShortcutFor({ key, shiftKey, altKey, ctrlKey, metaKey } = {}) {
  if (ctrlKey || metaKey) return null
  const k = (key || '').toLowerCase()
  if (!shiftKey && !altKey) return k === 'q' ? 'straighten' : null
  if (shiftKey && altKey) {
    if (k === 'w') return 'align:centerY'
    if (k === 's') return 'align:centerX'
    return null
  }
  if (shiftKey && !altKey) {
    if (k === 'a') return 'align:left'
    if (k === 'd') return 'align:right'
    if (k === 'w') return 'align:top'
    if (k === 's') return 'align:bottom'
  }
  return null
}
